import { useEffect, useState } from 'react';

/**
 * Two easing curves cover ~90% of tactile feel:
 * - snap: fast, no overshoot — press-down, drags, anything that must feel immediate
 * - pop: short overshoot — selection confirms, entrances, the moment something "lands"
 */
export const EASE_SNAP = 'cubic-bezier(0.16, 1, 0.3, 1)';
export const EASE_POP = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

/** Duration presets (ms) */
export const DURATION = {
  fast: 180,
  normal: 260,
  slow: 320,
  slowest: 420,
} as const;

/**
 * Reduced motion hook — per-component, NOT a global kill switch.
 * Some effects (opacity fades) may stay, others (transform pops) should drop.
 * Decide per-component what to disable.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);

    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return reduced;
}

/**
 * Returns motion-appropriate transition values based on reduced-motion preference.
 * Usage: const { duration, ease } = useMotion();
 * Then: transition={`transform ${duration}ms ${ease}`}
 */
export function useMotion() {
  const reduced = usePrefersReducedMotion();
  return {
    reduced,
    duration: reduced ? 0 : DURATION.normal,
    ease: reduced ? 'linear' : EASE_SNAP,
    durationPop: reduced ? 0 : DURATION.normal,
    easePop: reduced ? 'linear' : EASE_POP,
  };
}
